import { Marker } from '@react-google-maps/api'

// ─── INTERIM — aguardando decisão arquitetural ───────────────────────────────
// Esta camada substitui o antigo <HeatmapLayer>, que virou código morto: a classe
// google.maps.visualization.HeatmapLayer foi depreciada em maio/2025 e REMOVIDA na
// Maps JavaScript API v3.65 (maio/2026). O wrapper do @react-google-maps/api é fino
// sobre essa classe, então ele lança na montagem independentemente dos dados — não
// há conserto possível, só substituição.
//
// O que está aqui é a substituição de menor risco: <Marker> com símbolo de círculo,
// a mesma primitiva que components/Adm/MonitorsNow já usa (lá com PNG de pin). Um
// círculo pequeno faz histórico denso ler como nuvem de pontos em vez de parede de
// pins, mas NÃO é equivalente a um heatmap — não há gradiente de densidade.
//
// A visualização definitiva (deck.gl, versão nova da camada na Maps API, Polyline de
// trajeto) é decisão de painel/ADR, ainda em aberto. Por isso o contrato de props é
// mínimo: trocar o corpo deste arquivo deve ser suficiente, sem tocar em FindMonitors.
// ─────────────────────────────────────────────────────────────────────────────

// Teto de pontos renderizados. Cada <Marker> é um overlay real do Google Maps: a
// contagem custa caro em CPU e memória. 1000 mantém a tela responsiva e cobre o caso
// de uso com folga — acima disso o mapa começa a travar de forma perceptível, o que
// reintroduziria "tela inutilizável" por outro caminho. O truncamento NUNCA é
// silencioso: FindMonitors exibe quantos de quantos estão sendo mostrados.
export const MAX_RENDERED_POINTS = 1000

// Símbolos do Google Maps recebem cor como string em objeto JS — não enxergam CSS
// custom properties. Lemos o token em runtime para não duplicar o valor aqui e
// deixar a cor divergir de src/styles/global.js (fonte de verdade do DESIGN.md).
const TOKEN_FALLBACK = '#0c4891' // --blue
const readToken = (name, fallback) => {
  if (typeof window === 'undefined' || !document?.documentElement) return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

const isRenderable = (point) =>
  Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng))

/**
 * Camada de pontos do histórico de localização dos monitores.
 * Precisa ser renderizada como FILHO do <ZadMap>: o loader do script do Google Maps
 * (useJsApiLoader) vive dentro do ZadMap, então window.google só existe a partir dos
 * filhos dele. Tocar window.google.* no render de um componente que apenas *contém* o
 * ZadMap é uma corrida contra o fetch — foi essa corrida que derrubava esta tela.
 *
 * @param {Array<{lat:number, lng:number, userId?:string}>} points  pontos já filtrados
 * @param {number} maxPoints  teto de pontos renderizados
 * @param {number} scale      raio do círculo em pixels
 */
export const MonitorsPointsLayer = ({
  points = [],
  maxPoints = MAX_RENDERED_POINTS,
  scale = 4
}) => {
  if (!window.google?.maps) return null

  const color = readToken('--blue', TOKEN_FALLBACK)

  const icon = {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: color,
    fillOpacity: 0.65,
    strokeColor: '#ffffff',
    strokeOpacity: 0.9,
    strokeWeight: 1
  }

  return points
    .filter(isRenderable)
    .slice(0, maxPoints)
    .map((point, index) => (
      // Chave estável: key={Math.random()} (padrão herdado do MonitorsNow) força
      // desmontar e remontar todos os overlays a cada render — inviável nesta escala.
      <Marker
        key={`${point.userId ?? 'ponto'}-${point.lat}-${point.lng}-${index}`}
        position={{ lat: Number(point.lat), lng: Number(point.lng) }}
        icon={icon}
        clickable={false}
      />
    ))
}

export default MonitorsPointsLayer
